import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { crmAPI, API_BASE_URL } from '../../services/api';
import toast from 'react-hot-toast';
import { DocumentTextIcon, BanknotesIcon, CalendarDaysIcon, MapPinIcon, XMarkIcon, LockClosedIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Button from '../common/Button';
import { formatCurrency } from '../../utils/format';

interface PortalProject {
  id: string;
  title: string;
  owner_id?: string;
  photographer_name: string;
  status: string;
  total_amount: number;
  amount_paid: number;
  retainer_amount: number;
  retainer_paid: boolean;
  shoot_dates: any[];
  location: string;
  location_name: string;
  gallery_status: string;
  gallery_password?: string;
  invoices: any[];
  contracts: any[];
  lead_id?: string;
}

const ClientPortal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<PortalProject[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [portalTab, setPortalTab] = useState<'active' | 'history' | 'requests'>('active');
  const [selectedProject, setSelectedProject] = useState<PortalProject | null>(null);

  useEffect(() => {
    if (location.state?.selectedProjectId && projects.length > 0) {
      const foundProject = projects.find(p => p.id === location.state.selectedProjectId);
      if (foundProject) {
        setSelectedProject(foundProject);
      }
    }
  }, [location.state?.selectedProjectId, projects]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const { data } = await crmAPI.getClientPortalData();
      setProjects(data.data?.projects || []);
      setLeads(data.data?.leads || []);
    } catch (error) {
      toast.error('Failed to load portal sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRequest = async (leadId: string) => {
    try {
      await crmAPI.confirmPortalLead(leadId);
      toast.success('Inquiry confirmed successfully!');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to confirm request');
    }
  };

  const handleDeclineRequest = async (leadId: string) => {
    try {
      await crmAPI.declinePortalLead(leadId);
      toast.success('Inquiry declined.');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to decline request');
    }
  };

  const calculateAmountDue = (p: PortalProject) => {
    return Math.max(0, p.total_amount - p.amount_paid);
  };

  // Filter projects by active vs history
  const activeProjects = projects.filter(p =>
    ['booked', 'shooting', 'editing', 'proofing'].includes(p.status?.toLowerCase())
  );
  const historyProjects = projects.filter(p =>
    ['completed', 'cancelled'].includes(p.status?.toLowerCase())
  );

  const displayedProjects = portalTab === 'active' ? activeProjects : historyProjects;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  if (projects.length === 0 && leads.length === 0) {
    return (
      <div className="text-center py-20 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 mx-4 md:mx-0">
        <DocumentTextIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Sessions or Requests Yet</h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          You don't have any projects or collaboration requests linked to your account yet. When a creator adds you as a lead or client, your sessions will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-8 px-4 md:px-0">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">My Sessions</h2>
        <p className="text-gray-600 dark:text-gray-400 text-sm">View and manage all your past and upcoming photography sessions.</p>
      </div>

      {/* Tabs Switcher */}
      <div className="border-b border-gray-200 dark:border-gray-700 px-4 md:px-0">
        <div className="flex space-x-8">
          <button
            onClick={() => setPortalTab('active')}
            className={`pb-4 text-sm font-semibold border-b-2 transition-colors ${
              portalTab === 'active'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Active Sessions ({activeProjects.length})
          </button>
          <button
            onClick={() => setPortalTab('requests')}
            className={`pb-4 text-sm font-semibold border-b-2 transition-colors ${
              portalTab === 'requests'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Requests & Inquiries ({leads.length})
          </button>
          <button
            onClick={() => setPortalTab('history')}
            className={`pb-4 text-sm font-semibold border-b-2 transition-colors ${
              portalTab === 'history'
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Session History ({historyProjects.length})
          </button>
        </div>
      </div>

      {/* Grid Content */}
      {portalTab === 'requests' ? (
        leads.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 dark:bg-gray-800/30 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 mx-4 md:mx-0">
            <p className="text-gray-500 dark:text-gray-400">
              No pending collaboration requests found.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 md:px-0">
            {leads.map((lead) => (
              <div
                key={lead.id}
                className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-150 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col justify-between"
              >
                <div className="p-6 pb-4 flex flex-col gap-3">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="font-bold text-gray-900 dark:text-white capitalize">
                      {lead.interest_type || 'Shoot Request'}
                    </h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      lead.status === 'pending_confirmation' 
                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' 
                        : 'bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-400'
                    }`}>
                      {lead.status === 'pending_confirmation' ? 'Action Required' : lead.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    From: <span className="font-semibold text-gray-800 dark:text-gray-200">{lead.creator_name}</span>
                  </p>
                  
                  {lead.estimated_budget > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Budget: <span className="font-semibold text-teal-600 dark:text-teal-400">₹{lead.estimated_budget.toLocaleString('en-IN')}</span>
                    </p>
                  )}

                  {lead.expected_shoot_at && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Shoot Date: <span className="font-semibold text-gray-800 dark:text-gray-200">{new Date(lead.expected_shoot_at).toLocaleDateString()}</span>
                    </p>
                  )}

                  {lead.notes && (
                    <div className="mt-2 text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded-xl border border-gray-100 dark:border-gray-700 max-h-24 overflow-y-auto">
                      <p className="text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">{lead.notes}</p>
                    </div>
                  )}
                </div>

                {/* Actions for pending confirm */}
                {lead.status === 'pending_confirmation' ? (
                  <div className="p-4 bg-gray-50 dark:bg-gray-850/50 border-t border-gray-100 dark:border-gray-700 flex gap-2">
                    <button
                      onClick={() => handleConfirmRequest(lead.id)}
                      className="flex-1 py-2 px-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => handleDeclineRequest(lead.id)}
                      className="flex-1 py-2 px-3 bg-rose-50 dark:bg-rose-950/20 hover:bg-rose-100 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400 border border-rose-100 dark:border-rose-900/50 rounded-xl text-xs font-bold transition-all cursor-pointer"
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => {
                        navigate('/messages', {
                          state: {
                            openConversationWith: lead.owner_id,
                            crm_lead_id: lead.id,
                            userName: lead.creator_name,
                            userType: 'creator',
                            prefilledMessage: `Hey ${lead.creator_name}, let's chat about my collaboration request: ${lead.interest_type || 'Shoot'}.`
                          }
                        });
                      }}
                      className="py-2 px-3 bg-indigo-50 dark:bg-indigo-950/20 hover:bg-indigo-100 dark:hover:bg-indigo-950/40 text-indigo-650 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center"
                      title="Chat with Creator"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      In Discussion
                    </span>
                    <button
                      onClick={() => {
                        navigate('/messages', {
                          state: {
                            openConversationWith: lead.owner_id,
                            crm_lead_id: lead.id,
                            userName: lead.creator_name,
                            userType: 'creator',
                            prefilledMessage: `Hey ${lead.creator_name}, let's chat about our confirmed proposal.`
                          }
                        });
                      }}
                      className="py-1.5 px-3 bg-indigo-50 dark:bg-indigo-950/20 hover:bg-indigo-100 dark:hover:bg-indigo-950/40 text-indigo-650 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Chat Creator
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : displayedProjects.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-800/30 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 mx-4 md:mx-0">
          <p className="text-gray-500 dark:text-gray-400">
            No {portalTab === 'active' ? 'active' : 'past'} sessions found.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 md:px-0">
          {displayedProjects.map((project) => {
            const amountDue = calculateAmountDue(project);
            const primaryDate = project.shoot_dates?.[0];

            return (
              <div
                key={project.id}
                className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-150 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
              >
                {/* Header */}
                <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-755">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="font-bold text-gray-900 dark:text-white line-clamp-1">{project.title}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      project.status?.toLowerCase() === 'completed'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                        : project.status?.toLowerCase() === 'cancelled'
                        ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        : 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                    Photographer: <span className="font-medium text-gray-800 dark:text-gray-250">{project.photographer_name}</span>
                  </p>
                </div>

                {/* Body Details */}
                <div className="p-6 py-4 space-y-3 flex-grow text-xs text-gray-600 dark:text-gray-400">
                  {primaryDate && (
                    <div className="flex items-center gap-2">
                      <CalendarDaysIcon className="w-4 h-4 text-orange-500" />
                      <span>
                        {new Date(primaryDate.start_date).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  )}

                  {project.location_name && (
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4 text-orange-500" />
                      <span className="truncate">{project.location_name}</span>
                    </div>
                  )}

                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-between text-xs mt-2">
                    <div>
                      <span className="text-gray-400 block">Total Price</span>
                      <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(project.total_amount, 2)}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-gray-400 block">Amount Due</span>
                      <span className={`font-bold ${amountDue > 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {formatCurrency(amountDue, 2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer Action */}
                <div className="p-6 pt-0 flex gap-2">
                  <Button
                    onClick={() => setSelectedProject(project)}
                    className="flex-grow justify-center text-xs"
                    variant="outline"
                  >
                    View Details
                  </Button>
                  {project.owner_id && (
                    <button
                      onClick={() => {
                        navigate('/messages', {
                          state: {
                             openConversationWith: project.owner_id,
                             crm_project_id: project.id,
                             crm_lead_id: project.lead_id,
                             userName: project.photographer_name,
                             userType: 'creator',
                             prefilledMessage: `Hey ${project.photographer_name}, let's chat about our session: ${project.title}.`
                          }
                        });
                      }}
                      className="py-2 px-3 bg-indigo-50 dark:bg-indigo-950/20 hover:bg-indigo-100 dark:hover:bg-indigo-950/40 text-indigo-650 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 cursor-pointer shrink-0"
                      title="Chat Photographer"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Chat
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Session Details Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col border border-gray-150 dark:border-gray-700 animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-150 dark:border-gray-755 flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-black text-gray-900 dark:text-white">{selectedProject.title}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Photographer: <span className="font-medium text-gray-900 dark:text-gray-250">{selectedProject.photographer_name}</span>
                  </p>
                  <span className="text-gray-300">•</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    selectedProject.status?.toLowerCase() === 'completed'
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                      : selectedProject.status?.toLowerCase() === 'cancelled'
                      ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      : 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400'
                  }`}>
                    {selectedProject.status}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedProject(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-750 rounded-full transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8 overflow-y-auto">
              {/* Left Column: Details */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-3">Session Details</h4>
                  <div className="space-y-4 bg-gray-50 dark:bg-gray-900/40 rounded-2xl p-5 border border-gray-100 dark:border-gray-750">
                    {selectedProject.shoot_dates && selectedProject.shoot_dates.length > 0 && (
                      <div className="flex items-start gap-3">
                        <CalendarDaysIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-orange-500" />
                        <div>
                          <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">Shoot Date</span>
                          {selectedProject.shoot_dates.map((d, i) => (
                            <div key={i} className="text-sm font-bold text-gray-900 dark:text-white mt-0.5">
                              {new Date(d.start_date).toLocaleDateString(undefined, {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(selectedProject.location_name || selectedProject.location) && (
                      <div className="flex items-start gap-3 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                        <MapPinIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-orange-500" />
                        <div>
                          <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">Location</span>
                          <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5">{selectedProject.location_name}</div>
                          {selectedProject.location && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium leading-relaxed">{selectedProject.location}</div>}
                        </div>
                      </div>
                    )}

                    {selectedProject.gallery_password && (
                      <div className="flex items-start gap-3 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                        <LockClosedIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-orange-500" />
                        <div>
                          <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">Access Code</span>
                          <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5 flex flex-col sm:flex-row sm:items-center gap-2">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(selectedProject.gallery_password || '');
                                toast.success('Access code copied!');
                              }}
                              className="font-mono bg-white dark:bg-gray-850 hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-95 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 w-fit flex items-center gap-2 transition-all text-xs font-bold text-gray-900 dark:text-white group cursor-pointer focus:outline-none"
                              title="Click to copy access code"
                            >
                              <span>{selectedProject.gallery_password}</span>
                              <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-orange-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                              </svg>
                            </button>
                            <span className="text-xs text-gray-400 font-medium">(Same code is used to secure the gallery, invoices, and contracts)</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Gallery Section */}
                {selectedProject.gallery_status === 'published' && (
                  <div className="pt-4 border-t border-gray-150 dark:border-gray-750">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-3">Project Gallery</h4>
                    <div className="bg-teal-50/50 dark:bg-teal-950/10 border border-teal-100 dark:border-teal-900/30 rounded-2xl p-5 space-y-3">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Your public photos gallery has been published! Click the button below to view and download your proofed assets.
                      </p>
                      <Button
                        onClick={() => window.open(`/gallery/${selectedProject.id}`, '_blank')}
                        className="w-full justify-center"
                      >
                        View Public Gallery
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Invoices & Contracts */}
              <div className="space-y-6">
                {/* Payments Card */}
                <div>
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-3">Payments</h4>
                  <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-755 rounded-2xl p-5 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Total Price</span>
                      <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(selectedProject.total_amount, 2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Amount Paid</span>
                      <span className="font-semibold text-green-600 dark:text-green-450">{formatCurrency(selectedProject.amount_paid, 2)}</span>
                    </div>
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-between font-bold text-base">
                      <span className="text-gray-900 dark:text-white">Amount Due</span>
                      <span className={calculateAmountDue(selectedProject) > 0 ? 'text-red-500' : 'text-green-500'}>
                        {formatCurrency(calculateAmountDue(selectedProject), 2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Invoices List */}
                <div>
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-3">Invoices</h4>
                  {selectedProject.invoices && selectedProject.invoices.filter((inv: any) => inv.status !== 'draft').length > 0 ? (
                    <div className="space-y-2">
                      {selectedProject.invoices.filter((inv: any) => inv.status !== 'draft').map((inv) => (
                        <div
                          key={inv.id}
                          onClick={() => window.open(`/invoice/${inv.id}`, '_blank')}
                          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-150 dark:border-gray-700 text-sm shadow-sm cursor-pointer hover:border-teal-200 dark:hover:border-teal-900 group transition-all"
                        >
                          <div>
                            <span className="font-bold text-gray-900 dark:text-white group-hover:text-teal-500 transition-colors">{inv.invoice_number}</span>
                            <span className="ml-2 text-xs text-gray-400 font-medium">({formatCurrency(inv.total_amount, 2)})</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${
                              inv.status === 'paid'
                                ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                            }`}>
                              {inv.status}
                            </span>
                            <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-all" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50/50 dark:bg-gray-900/20 py-3 text-center rounded-xl border border-dashed border-gray-250 dark:border-gray-700">No invoices available</p>
                  )}
                </div>

                {/* Contracts List */}
                <div>
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-3">Contracts</h4>
                  {selectedProject.contracts && selectedProject.contracts.filter((c: any) => c.status !== 'draft').length > 0 ? (
                    <div className="space-y-2">
                      {selectedProject.contracts.filter((c: any) => c.status !== 'draft').map((contract) => (
                        <div
                          key={contract.id}
                          onClick={() => window.open(`/crm/contract/${contract.id}`, '_blank')}
                          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-150 dark:border-gray-700 text-sm shadow-sm cursor-pointer hover:border-teal-200 dark:hover:border-teal-900 group transition-all"
                        >
                          <div className="truncate pr-4 flex-1">
                            <span className="font-bold text-gray-900 dark:text-white truncate block group-hover:text-teal-500 transition-colors">{contract.title}</span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${
                              contract.status === 'signed'
                                ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                            }`}>
                              {contract.status}
                            </span>
                            <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-all" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50/50 dark:bg-gray-900/20 py-3 text-center rounded-xl border border-dashed border-gray-250 dark:border-gray-700">No contracts available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortal;
